import { useEffect, useState, startTransition } from "react";
import Cookies from "js-cookie";

/**
 * Dark-theme manager.
 *
 * The ONLY mechanism that can activate dark styling anywhere in the app is the
 * `data-theme="dark"` attribute on <html>. Every dark rule in src/theme/dark.css
 * is gated behind it, so when the attribute is absent (the default) the light
 * theme is byte-identical to the pre-dark-theme build.
 *
 * Persistence is dual: localStorage (fast pre-paint read in public/index.html)
 * plus a cookie fallback, because logout runs `localStorage.clear()` (App.js
 * inactivity logout + Header logout) and the preference must survive it.
 */

const STORAGE_KEY = "qv-theme";
const COOKIE_KEY = "qv_theme";
const EVENT_NAME = "qv-theme-change";
export const THEME_LIGHT = "light";
export const THEME_DARK = "dark";

/**
 * Dark FLAVORS — curated tints of the neutral surface ramp. Hue/saturation
 * differ per flavor; LIGHTNESS is identical to graphite, so every contrast
 * ratio tuned for dark carries over unchanged. CSS side: genDarkTokens.js
 * emits `html[data-theme="dark"][data-qv-flavor="<id>"]` override blocks;
 * graphite is the default and has no block/attribute.
 *
 * page/paper/divider/swatch are LITERAL hex on purpose (never var() — those
 * break MUI's color math and any JS string logic; see dark_theme_retarget_gaps
 * gap 7). They MUST mirror the generator's retint() output, which prints them
 * on every run: `node scripts/genDarkTokens.js`.
 */
const FLAVOR_STORAGE_KEY = "qv-theme-flavor";
const FLAVOR_COOKIE_KEY = "qv_theme_flavor";
const FLAVOR_EVENT = "qv-flavor-change";
export const FLAVOR_DEFAULT = "graphite";
// `swatch` is a POSTER colour for the picker dot only — deliberately more
// saturated/lighter than the real surfaces so the four tones are clearly
// distinguishable at 18px. It is never painted on any surface.
export const DARK_FLAVORS = [
  { id: "graphite", label: "Graphite", swatch: "#59616e", page: "#0c0f14", paper: "#151a21", divider: "#29303d" },
  { id: "navy",     label: "Midnight Navy", swatch: "#3554a5", page: "#0a0e16", paper: "#111725", divider: "#222d44" },
  { id: "slate",    label: "Deep Slate", swatch: "#4d6b85", page: "#0e1012", paper: "#171b1f", divider: "#2d3339" },
  { id: "espresso", label: "Espresso", swatch: "#7d5535", page: "#130f0d", paper: "#201a16", divider: "#3b312b" },
];
const FLAVOR_IDS = DARK_FLAVORS.map((f) => f.id);

/**
 * LIGHT tones. "default" is the untouched light theme — no attribute, no CSS,
 * mechanically identical to what shipped before theming existed. "warm" is
 * parchment (hue-matched to the Sale Register light theme), defined entirely
 * in src/theme/light.generated.css behind
 * `html[data-qv-light="warm"]:not([data-theme="dark"])`.
 * page/paper are LITERAL hex mirrored from the generator's console output
 * (`node scripts/genDarkTokens.js`) — same gap-7 rule as DARK_FLAVORS.
 * `swatch` is a poster colour for the picker dot only.
 */
const LIGHT_STORAGE_KEY = "qv-light-tone";
const LIGHT_COOKIE_KEY = "qv_light_tone";
const LIGHT_EVENT = "qv-light-tone-change";
export const LIGHT_DEFAULT = "default";
export const LIGHT_TONES = [
  { id: "default", label: "Classic Light", swatch: "#e9edf2", page: null, paper: null },
  { id: "warm",    label: "Warm Light", swatch: "#ecd9b5", page: "#fcf6ec", paper: "#fcf8ef" },
];
const LIGHT_IDS = LIGHT_TONES.map((t) => t.id);

export function getStoredLightTone() {
  let stored = null;
  try {
    stored = window.localStorage.getItem(LIGHT_STORAGE_KEY);
  } catch (e) {
    /* storage unavailable */
  }
  if (!LIGHT_IDS.includes(stored)) {
    stored = Cookies.get(LIGHT_COOKIE_KEY) || null;
  }
  return LIGHT_IDS.includes(stored) ? stored : LIGHT_DEFAULT;
}

export function getLightToneSurfaces(toneId) {
  return LIGHT_TONES.find((t) => t.id === toneId) || LIGHT_TONES[0];
}

function notifyAppearance(name, detail) {
  // The CSS attribute is already on <html>. The React/MUI theme update
  // re-renders the whole tree; running it as a transition lets the browser
  // paint the new theme and accept clicks instead of freezing the tab.
  startTransition(() => {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  });
}

function persistPreference(storageKey, cookieKey, value) {
  try {
    window.localStorage.setItem(storageKey, value);
  } catch (e) {
    /* storage unavailable */
  }
  Cookies.set(cookieKey, value, { expires: 365 });
}

export function setStoredLightTone(toneId) {
  const value = LIGHT_IDS.includes(toneId) ? toneId : LIGHT_DEFAULT;
  persistPreference(LIGHT_STORAGE_KEY, LIGHT_COOKIE_KEY, value);
  applyThemeAttribute(window.location.pathname);
  notifyAppearance(LIGHT_EVENT, value);
}

export function getStoredFlavor() {
  let stored = null;
  try {
    stored = window.localStorage.getItem(FLAVOR_STORAGE_KEY);
  } catch (e) {
    /* storage unavailable */
  }
  if (!FLAVOR_IDS.includes(stored)) {
    stored = Cookies.get(FLAVOR_COOKIE_KEY) || null;
  }
  return FLAVOR_IDS.includes(stored) ? stored : FLAVOR_DEFAULT;
}

/** Surfaces the MUI dark palette must use for the active flavor. */
export function getFlavorSurfaces(flavorId) {
  return DARK_FLAVORS.find((f) => f.id === flavorId) || DARK_FLAVORS[0];
}

export function setStoredFlavor(flavorId) {
  const value = FLAVOR_IDS.includes(flavorId) ? flavorId : FLAVOR_DEFAULT;
  persistPreference(FLAVOR_STORAGE_KEY, FLAVOR_COOKIE_KEY, value);
  applyThemeAttribute(window.location.pathname);
  notifyAppearance(FLAVOR_EVENT, value);
}

/**
 * Write theme + flavor/tone, then restyle ONCE.
 * The picker used to call setStoredTheme and setStoredFlavor back to back.
 * Each call flipped `data-theme` / `data-qv-flavor` on <html>, and each flip
 * invalidates every dark rule in the document. Two flips meant two full
 * style recalcs and a visible half-applied theme in between.
 */
export function setStoredAppearance({ theme, flavor, lightTone } = {}) {
  let nextTheme;
  let nextFlavor;
  let nextTone;
  if (theme) {
    nextTheme = theme === THEME_DARK ? THEME_DARK : THEME_LIGHT;
    persistPreference(STORAGE_KEY, COOKIE_KEY, nextTheme);
  }
  if (flavor) {
    nextFlavor = FLAVOR_IDS.includes(flavor) ? flavor : FLAVOR_DEFAULT;
    persistPreference(FLAVOR_STORAGE_KEY, FLAVOR_COOKIE_KEY, nextFlavor);
  }
  if (lightTone) {
    nextTone = LIGHT_IDS.includes(lightTone) ? lightTone : LIGHT_DEFAULT;
    persistPreference(LIGHT_STORAGE_KEY, LIGHT_COOKIE_KEY, nextTone);
  }
  applyThemeAttribute(window.location.pathname);
  startTransition(() => {
    if (nextTheme) {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: nextTheme }));
    }
    if (nextFlavor) {
      window.dispatchEvent(new CustomEvent(FLAVOR_EVENT, { detail: nextFlavor }));
    }
    if (nextTone) {
      window.dispatchEvent(new CustomEvent(LIGHT_EVENT, { detail: nextTone }));
    }
  });
}

/**
 * Routes that must NEVER render dark (react-router paths, i.e. without the
 * /merchants basename). Keep in sync with the pre-paint script in
 * public/index.html, which sees the full window.location.pathname.
 */
const EXCLUDED_PATHS = [
  "/login",
  "/forgot-password",
  "/reset-password",
  "/web-register",
  "/print-purchase-order",
  "/stocktake/print-stocktake-report",
];

export function isThemeExcludedPath(pathname) {
  if (!pathname) return false;
  // Normalize: accept both router paths and full window paths.
  const path = pathname.startsWith("/merchants")
    ? pathname.slice("/merchants".length) || "/"
    : pathname;
  return EXCLUDED_PATHS.some(
    (p) => path === p || path.startsWith(`${p}/`)
  );
}

export function getStoredTheme() {
  let stored = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    /* storage unavailable */
  }
  if (stored !== THEME_DARK && stored !== THEME_LIGHT) {
    stored = Cookies.get(COOKIE_KEY) || null;
  }
  return stored === THEME_DARK ? THEME_DARK : THEME_LIGHT;
}

export function setStoredTheme(theme) {
  const value = theme === THEME_DARK ? THEME_DARK : THEME_LIGHT;
  persistPreference(STORAGE_KEY, COOKIE_KEY, value);
  applyThemeAttribute(window.location.pathname);
  notifyAppearance(EVENT_NAME, value);
}

/**
 * Apply or remove the data-theme attribute for the current route. Idempotent;
 * called on every route change and on every toggle.
 *
 * `allowDark` is the gate for EVERY non-default appearance (dark AND the warm
 * light tone): App.js passes "signed in" (any login_type), so it is false only
 * while auth is loading or signed out. Defaults to true so the setStored*()
 * internal calls (only reachable from the picker) keep working unchanged.
 */
let themeSwitchFrame = 0;

function beginThemeSwitch(root) {
  // Many controls use `transition: all` / `transition: background`. A theme
  // change would otherwise tween every one of those for ~200ms, which is the
  // choppy crawl. Hold transitions off until the new colours have painted.
  root.setAttribute("data-qv-theme-switching", "");
  const frame = ++themeSwitchFrame;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (frame !== themeSwitchFrame) return;
      root.removeAttribute("data-qv-theme-switching");
    });
  });
}

export function applyThemeAttribute(pathname, allowDark = true) {
  const wantDark =
    allowDark &&
    getStoredTheme() === THEME_DARK &&
    !isThemeExcludedPath(pathname);
  const root = document.documentElement;
  const nextTheme = wantDark ? THEME_DARK : null;
  const flavor = getStoredFlavor();
  const nextFlavor = wantDark && flavor !== FLAVOR_DEFAULT ? flavor : null;
  const tone = getStoredLightTone();
  const wantTone =
    !wantDark &&
    allowDark &&
    tone !== LIGHT_DEFAULT &&
    !isThemeExcludedPath(pathname);
  const nextTone = wantTone ? tone : null;

  const themeChanged = (root.getAttribute("data-theme") || null) !== nextTheme;
  const flavorChanged = (root.getAttribute("data-qv-flavor") || null) !== nextFlavor;
  const toneChanged = (root.getAttribute("data-qv-light") || null) !== nextTone;
  if (!themeChanged && !flavorChanged && !toneChanged) return;

  beginThemeSwitch(root);
  if (themeChanged) {
    if (nextTheme) root.setAttribute("data-theme", nextTheme);
    else root.removeAttribute("data-theme");
  }
  if (flavorChanged) {
    if (nextFlavor) root.setAttribute("data-qv-flavor", nextFlavor);
    else root.removeAttribute("data-qv-flavor");
  }
  if (toneChanged) {
    if (nextTone) root.setAttribute("data-qv-light", nextTone);
    else root.removeAttribute("data-qv-light");
  }
}

/**
 * Printing must ALWAYS be light.
 *
 * `data-theme` lives on <html>, so it stays set while the browser renders the
 * print document — a merchant printing a tender report or purchase order in
 * dark mode would get dark backgrounds (unreadable, and a toner sink).
 *
 * Stripping the attribute around the print neutralises every dark rule at once,
 * which is far more reliable than trying to reset ~1,400 generated tokens
 * inside an @media print block. Registered once at module load.
 */
let printGuardInstalled = false;
export function installPrintGuard() {
  if (printGuardInstalled || typeof window === "undefined") return;
  printGuardInstalled = true;

  let restore = false;
  let restoreTone = null;
  const before = () => {
    const root = document.documentElement;
    if (root.getAttribute("data-theme") === THEME_DARK) {
      root.removeAttribute("data-theme");
      restore = true;
    }
    // Warm light must not tint printed paper either — print is CLASSIC light.
    const tone = root.getAttribute("data-qv-light");
    if (tone) {
      root.removeAttribute("data-qv-light");
      restoreTone = tone;
    }
  };
  const after = () => {
    if (restore) {
      document.documentElement.setAttribute("data-theme", THEME_DARK);
      restore = false;
    }
    if (restoreTone) {
      document.documentElement.setAttribute("data-qv-light", restoreTone);
      restoreTone = null;
    }
  };

  window.addEventListener("beforeprint", before);
  window.addEventListener("afterprint", after);

  // Safari/legacy fallback: beforeprint is not universally fired.
  if (typeof window.matchMedia === "function") {
    const mq = window.matchMedia("print");
    const onChange = (e) => (e.matches ? before() : after());
    if (typeof mq.addEventListener === "function") mq.addEventListener("change", onChange);
    else if (typeof mq.addListener === "function") mq.addListener(onChange);
  }
}

/** React hook: [theme, setTheme]. `theme` is the stored preference. */
export function useQvTheme() {
  const [theme, setThemeState] = useState(getStoredTheme);

  useEffect(() => {
    const onChange = (e) => setThemeState(e.detail);
    window.addEventListener(EVENT_NAME, onChange);
    return () => window.removeEventListener(EVENT_NAME, onChange);
  }, []);

  return [theme, setStoredTheme];
}

/** React hook: [toneId, setTone]. Only meaningful while LIGHT is active. */
export function useQvLightTone() {
  const [tone, setToneState] = useState(getStoredLightTone);

  useEffect(() => {
    const onChange = (e) => setToneState(e.detail);
    window.addEventListener(LIGHT_EVENT, onChange);
    return () => window.removeEventListener(LIGHT_EVENT, onChange);
  }, []);

  return [tone, setStoredLightTone];
}

/** React hook: [flavorId, setFlavor]. Only meaningful while dark is active. */
export function useQvFlavor() {
  const [flavor, setFlavorState] = useState(getStoredFlavor);

  useEffect(() => {
    const onChange = (e) => setFlavorState(e.detail);
    window.addEventListener(FLAVOR_EVENT, onChange);
    return () => window.removeEventListener(FLAVOR_EVENT, onChange);
  }, []);

  return [flavor, setStoredFlavor];
}
